import { useToastStore, type ToastAction } from '@/store/toast.store';
import type { ReactNode } from 'react';

export { useToastStore } from '@/store/toast.store';
export type {
  ToastType, Toast, ToastAction, ToastVariant, ToastPosition, ToastActionButton,
} from '@/store/toast.store';

export const toast = {
  success: (title: string, message?: string, action?: ToastAction) => {
    useToastStore.getState().addToast({
      type: 'success',
      message: message ? `${title}: ${message}` : title,
      duration: 3000,
      action,
    });
  },
  /**
   * `opts.persist` controls whether this error is also written to the
   * operator's notification history. Errors persist by default (see
   * toast.store) so a failure noticed later is still recoverable; pass
   * `persist: false` for feedback the user can act on immediately and that has
   * no value afterwards — form validation being the case that motivated it
   * (F-070).
   */
  error: (title: string, message?: string, action?: ToastAction, opts?: { persist?: boolean }) => {
    useToastStore.getState().addToast({
      type: 'error',
      message: message ? `${title}: ${message}` : title,
      duration: 5000,
      action,
      ...(opts?.persist === undefined ? {} : { persist: opts.persist }),
    });
  },
  warning: (title: string, message?: string, action?: ToastAction) => {
    useToastStore.getState().addToast({
      type: 'warning',
      message: message ? `${title}: ${message}` : title,
      duration: 4000,
      action,
    });
  },
  info: (title: string, message?: string, action?: ToastAction) => {
    useToastStore.getState().addToast({
      type: 'info',
      message: message ? `${title}: ${message}` : title,
      duration: 4000,
      action,
    });
  },

  notify: (options: {
    title: string;
    body?: string;
    priority?: 'urgent' | 'high' | 'normal' | 'low';
    icon?: string;
    onClick?: () => void;
    position?: 'top-right' | 'bottom-left' | 'bottom-right';
    duration?: number;
    requiresManualDismiss?: boolean;
  }) => {
    useToastStore.getState().addToast({
      type: 'info',
      variant: 'rich',
      message: options.title,
      title: options.title,
      body: options.body,
      priority: options.priority ?? 'normal',
      icon: options.icon,
      onClick: options.onClick,
      position: options.position ?? 'bottom-left',
      duration: options.duration ?? 0,
      requiresManualDismiss: options.requiresManualDismiss,
    });
  },

  action: (options: {
    title?: string;
    message: string;
    actions: Array<{
      label: string;
      onClick: () => void | Promise<void>;
      variant?: 'primary' | 'success' | 'danger' | 'ghost';
      loadingLabel?: string;
    }>;
    position?: 'top-right' | 'bottom-left' | 'bottom-right';
    duration?: number;
    icon?: string;
  }) => {
    useToastStore.getState().addToast({
      type: 'info',
      variant: 'action',
      message: options.message,
      title: options.title,
      actions: options.actions,
      position: options.position ?? 'bottom-right',
      duration: options.duration ?? 30000,
      icon: options.icon,
    });
  },

  custom: (
    component: ReactNode,
    options?: {
      position?: 'top-right' | 'bottom-left' | 'bottom-right';
      duration?: number;
      requiresManualDismiss?: boolean;
    }
  ) => {
    useToastStore.getState().addToast({
      type: 'info',
      variant: 'standard',
      message: '',
      component,
      position: options?.position ?? 'top-right',
      duration: options?.duration ?? 5000,
      requiresManualDismiss: options?.requiresManualDismiss,
    });
  },
};
